import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { Brief, PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { useEntities } from "../../lib/data.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { usePeople } from "../../lib/people.ts";
import { useResource } from "../../lib/resource.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Chip } from "../../ui/Chip.tsx";
import { Fold } from "../../ui/Fold.tsx";
import { Async } from "../../ui/misc.tsx";
import { Table } from "../../ui/Table.tsx";
import { ItemCards } from "../items/cards.tsx";
import { EntityLabel } from "../items/entityLabel.tsx";
import { ItemsHead, Rows } from "../items/rows.tsx";

/** How many more links a field lists each time it's asked to show more. */
const PAGE = 20;

/**
 * An item linked to, as its link shows it: a link to its page; struck through, its id, when it's gone; `title` in
 * italics when it's private to others (nothing to open). `linkClass`: the link's look.
 */
export function LinkedTarget(p: {
  space: SpaceOut;
  id: string;
  brief: Brief | undefined;
  title: string;
  linkClass: string;
}) {
  if (p.brief?.missing) {
    return <span class="text-red-700 line-through">{p.id}</span>;
  }
  return p.brief?.hidden ? (
    <span class="text-zinc-500 italic">{p.title}</span>
  ) : (
    <Link href={spaceUrl(p.space, `/items/${p.id}`)} class={p.linkClass}>
      {p.title}
    </Link>
  );
}

/**
 * A field's links as the item lists show items: a table or cards (the field's choice), in the field's order, each
 * taken off with its ×; more on asking ("Show more", added below: no pages to go between). Of several entities (a
 * field that doesn't say which), a group each, with that entity's columns. The ones you can't see (or gone) stay as a
 * word each.
 */
export function LinkedItems(p: {
  space: SpaceOut;
  ids: readonly string[];
  /** What the item knows of its links (titles; private or gone). */
  links: readonly Brief[];
  /** The entity its links are of (its columns), if the field says. */
  target: string | null;
  /** Shown as a table, or as cards. */
  view: "table" | "cards";
  onRemove: ((id: string) => void) | undefined;
}) {
  const { view } = p;
  const [shown, setShown] = useState(PAGE);
  // Groups opened or closed, kept as more come (drawn again).
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const entities = useEntities(p.space);
  const members = usePeople();
  const ids = p.ids.slice(0, shown);
  const r = useResource(`linked:${p.space.id}:${ids.join(",")}`, (signal) =>
    api.get<PageItemRow>(spaceApi(p.space, "/items"), { ids: ids.join(","), limit: ids.length }, signal),
  );
  const all = entities.data ?? [];
  const list = (page: PageItemRow, rows: PageItemRow["data"], entityId: string | null | undefined) => {
    const entity = all.find((e) => e.id === entityId);
    const common = { space: p.space, entity, entities: all, members, onRemove: p.onRemove };
    const part: PageItemRow = { ...page, data: rows };
    return view === "table" ? (
      <Table>
        <ItemsHead entity={entity} removable={p.onRemove !== undefined} />
        <Rows page={part} {...common} />
      </Table>
    ) : (
      <ItemCards page={part} {...common} />
    );
  };
  return (
    <Async r={r}>
      {(page) => {
        const byId = new Map(page.data.map((row) => [row.id, row]));
        const rows = ids.flatMap((id) => byId.get(id) ?? []);
        // Not listed: private to others, or gone.
        const rest = ids.filter((id) => !byId.has(id));
        // A field of one entity (or whose links are all one): one list; else a group per entity, in order of first.
        const kinds = [...new Set(rows.map((row) => row.entity ?? null))];
        const one = p.target !== null || kinds.length <= 1;
        return (
          <div class="flex flex-col gap-2">
            {rows.length > 0 && one ? list(page, rows, p.target ?? kinds[0]) : null}
            {rows.length > 0 && !one
              ? kinds.map((k) => {
                  const of = rows.filter((row) => (row.entity ?? null) === k);
                  const entity = all.find((e) => e.id === k);
                  // Each entity's folded as the field's links are (short ones open).
                  return (
                    <Fold
                      key={k ?? ""}
                      name={entity?.name ?? t("items.noEntity")}
                      label={<EntityLabel entity={entity} large={true} />}
                      nested={true}
                      count={of.length}
                      open={opened[k ?? ""]}
                      onToggle={(o) => setOpened({ ...opened, [k ?? ""]: o })}
                    >
                      {() => list(page, of, k)}
                    </Fold>
                  );
                })
              : null}
            {rest.length > 0 ? (
              <ul class="flex flex-wrap gap-2">
                {rest.map((id) => {
                  const b = p.links.find((l) => l.id === id);
                  return (
                    <li key={id}>
                      <Chip
                        size="md"
                        {...(p.onRemove
                          ? {
                              onRemove: () => p.onRemove?.(id),
                              removeLabel: t("fields.unlink", {
                                title: b?.hidden ? t("item.privateItem") : b?.title || id,
                              }),
                            }
                          : {})}
                      >
                        <LinkedTarget
                          space={p.space}
                          id={id}
                          brief={b}
                          title={b?.hidden ? t("item.privateItem") : b?.title || t("common.untitled")}
                          linkClass="text-blue-700 hover:underline"
                        />
                      </Chip>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {p.ids.length > shown ? (
              <div>
                <Button size="sm" onClick={() => setShown(shown + PAGE)}>
                  {t("items.tree.more")}
                </Button>
              </div>
            ) : null}
          </div>
        );
      }}
    </Async>
  );
}
